// EN-1476: a light Halloween theme, live from Oct 20, 2026 00:00 Mountain until Nov 1, 2026 00:00
// Mountain (the midnight that ends Oct 31). Every themed piece checks isHalloween(), or the
// `.halloween-nav` class Navbar adds when it is true, so the app goes back to normal on its own with
// no revert deploy. The window is absolute (not browser local time) and pinned to 2026 on purpose:
// it does not come back next October.
//
// Cleanup checklist (delete after Nov 1):
//   - this file and __tests__/halloween.test.ts
//   - public/halloween-logo.png, public/halloween-scout.png
//   - Login.tsx, LockScreen.tsx, Navbar.tsx: logo swap (Navbar also the night bar and title color)
//   - src/app/index.css: the `.halloween-nav` block
//   - navIconButton.ts: the `nav-icon-button` / `nav-icon-button-filled` hook classes
//   - ConfettiElement.tsx: drawShape and the shape helpers
//   - useThinkingMessage.ts: HALLOWEEN_MESSAGES (shown by EditorLoadingPage, BuilderLoadingPage and
//     Scout's ChatMessage)
//   - EditorLoadingPage.tsx: the second message, plus its EN-1476 block in EditorLoadingPage.test.tsx
//   - PermissionWrapper.tsx: the Scout opener, plus its EN-1476 test in
//     PermissionWrapper.floatingButtons.test.tsx

/** Set this localStorage key to "1" to preview the theme outside the window. */
export const HALLOWEEN_PREVIEW_KEY = "hd_preview_halloween";

// Oct 20, 2026 00:00 MDT (UTC-6). Already past when this ships, so the theme is live on deploy.
const HALLOWEEN_START_MS = Date.parse("2026-10-20T00:00:00-06:00");
// Nov 1, 2026 00:00 MDT. Still UTC-6 at midnight: DST ends at 2am that morning.
const HALLOWEEN_END_MS = Date.parse("2026-11-01T00:00:00-06:00");

const isPreviewForced = (): boolean => {
    try {
        return window.localStorage.getItem(HALLOWEEN_PREVIEW_KEY) === "1";
    } catch {
        return false;
    }
};

export const isHalloween = (d: Date = new Date()): boolean => {
    if (isPreviewForced()) return true;
    const t = d.getTime();
    return t >= HALLOWEEN_START_MS && t < HALLOWEEN_END_MS;
};

export const HALLOWEEN_LOGO_SRC = "/halloween-logo.png";

/**
 * For the logo on a dark background (navbar, lock screen): a 1px light outline plus a soft glow, so
 * the dark purple hat, bat and spider web stay visible instead of melting into the background.
 */
export const HALLOWEEN_LOGO_ON_DARK_CLASS =
    "[filter:drop-shadow(1px_0_0_#e9e2f1)_drop-shadow(-1px_0_0_#e9e2f1)_drop-shadow(0_1px_0_#e9e2f1)_drop-shadow(0_-1px_0_#e9e2f1)_drop-shadow(0_0_6px_rgba(255,255,255,0.55))]";

/** Perry in a witch hat for the Scout opener: 324x328, the mascot SVG's 81x82 box at 4x, no shadow. */
export const HALLOWEEN_SCOUT_SRC = "/halloween-scout.png";

/**
 * In EditorLoadingPage, replaces "Still working hard..." as the second message, so the theme shows at 7s. The
 * fun pool only starts at 14s, after most documents (median ~11s) have already finished.
 */
export const HALLOWEEN_LOADING_LINE = "Brewing your SOAP...";
